import React, { useState, useRef, useCallback, useEffect } from 'react';
import { T, FONT, radius, tint } from './poyais-ui/theme';
import { Card, Tile, Button, Eyebrow, IconChip, Grid } from './poyais-ui/primitives';

interface Voice { id:string; name:string; preview_url?:string }
interface HistoryItem { id:string; text:string; voiceName:string; createdAt:string; audioUrl?:string; status:'pending'|'done'|'error' }
type View = 'home' | 'tts' | 'clone';

const MODES = [
  { key:'tts',   label:'Metin → Ses',  icon:'🔊', desc:'Metninizi seçtiğiniz sesle seslendirin.' },
  { key:'clone', label:'Ses Klonlama', icon:'🎭', desc:'Referans ses yükleyerek kendi sesinizi klonlayın.' },
];

const Spinner:React.FC=()=>(
  <span style={{display:'inline-block',width:13,height:13,border:`2px solid ${tint(T.accent,25)}`,borderTopColor:T.accent,borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
);

const AISesTab:React.FC<{config?:any;creditBalance?:number;onSpend?:()=>void}>=({creditBalance:creditBalanceProp=0})=>{
  const [view,setView]           = useState<View>('home');
  const [voices,setVoices]       = useState<Voice[]>([]);
  const [voicesLoading,setVL]    = useState(true);
  const [selectedVoice,setSV]    = useState('');
  const [previewingId,setPrevId] = useState<string|null>(null);
  const [text,setText]           = useState('');
  const [cloneFile,setCloneFile] = useState<File|null>(null);
  const [cloneText,setCloneText] = useState('');
  const [stability,setStability] = useState(0.5);
  const [similarity,setSimilarity] = useState(0.75);
  const [loading,setLoading]     = useState(false);
  const [history,setHistory]     = useState<HistoryItem[]>([]);
  const [playingId,setPlayingId] = useState<string|null>(null);
  const audioRefs  = useRef<Record<string,HTMLAudioElement>>({});
  const previewRef = useRef<HTMLAudioElement|null>(null);
  const fileRef    = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem('poyais_token') || '';

  useEffect(()=>{
    fetch('/api/v1/kreativ/ses/voices',{headers:{'Authorization':'Bearer '+token()}})
      .then(r=>r.json()).then(d=>{ setVoices(d.voices||[]); if(d.voices?.length) setSV(d.voices[0].id); })
      .catch(()=>{}).finally(()=>setVL(false));
  },[]);

  useEffect(()=>{
    const s=localStorage.getItem('poyais_ses_history');
    if(s){try{setHistory(JSON.parse(s));}catch{}}
  },[]);

  const saveHistory=useCallback((items:HistoryItem[])=>{
    setHistory(items);
    localStorage.setItem('poyais_ses_history',JSON.stringify(items.slice(0,20)));
  },[]);

  const previewVoice=(v:Voice)=>{
    if(!v.preview_url) return;
    if(previewRef.current) previewRef.current.pause();
    if(previewingId===v.id){ setPrevId(null); return; }
    const a=new Audio(v.preview_url);
    previewRef.current=a; a.play(); setPrevId(v.id); a.onended=()=>setPrevId(null);
  };

  const handleSynthesize=async()=>{
    if(!text.trim()) return;
    if(view==='clone'&&!cloneFile) return;
    if(creditBalanceProp < 2) {
      alert('Yetersiz kredi. Ses üretimi 2 kredi gerektirir. Lütfen kredi yükleyin.');
      return;
    }
    setLoading(true);
    const id=`ses-${Date.now()}`;
    const vName=voices.find(v=>v.id===selectedVoice)?.name||selectedVoice;
    const entry:HistoryItem={id,text:text.slice(0,80)+(text.length>80?'…':''),voiceName:view==='clone'?`Klonlama: ${cloneFile?.name}`:vName,createdAt:new Date().toLocaleString('tr-TR'),status:'pending'};
    saveHistory([entry,...history]);
    try{
      let res:Response;
      if(view==='tts'){
        res=await fetch('/api/v1/kreativ/ses/synthesize',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+token()},body:JSON.stringify({text,voice_id:selectedVoice,stability,similarity_boost:similarity,client_id:parseInt(localStorage.getItem('poyais_active_client')||'1')})});
      } else {
        const fd=new FormData();
        fd.append('text',text); fd.append('prompt_wav',cloneFile!);
        if(cloneText) fd.append('prompt_text',cloneText);
        fd.append('stability',String(stability)); fd.append('similarity_boost',String(similarity));
        res=await fetch('/api/v1/kreativ/ses/clone',{method:'POST',headers:{'Authorization':'Bearer '+token()},body:fd});
      }
      if(!res.ok) throw new Error();
      const blob=await res.blob();
      const audioUrl=URL.createObjectURL(blob);
      saveHistory([{...entry,status:'done',audioUrl},...history]);
    }catch{ saveHistory([{...entry,status:'error'},...history]); }
    finally{ setLoading(false); }
  };

  const togglePlay=(item:HistoryItem)=>{
    if(!item.audioUrl) return;
    const audio=audioRefs.current[item.id]??(audioRefs.current[item.id]=new Audio(item.audioUrl));
    if(playingId===item.id){ audio.pause(); setPlayingId(null); }
    else{ Object.values(audioRefs.current).forEach(a=>a.pause()); audio.play(); setPlayingId(item.id); audio.onended=()=>setPlayingId(null); }
  };

  const CreditBadge=()=>(
    <div style={{display:'flex',justifyContent:'flex-end',padding:'12px 16px 0'}}>
      <div style={{display:'inline-flex',alignItems:'center',gap:10,background:tint(T.accent,8),border:`1px solid ${tint(T.accent,20)}`,borderRadius:radius.md,padding:'8px 16px'}}>
        <span style={{fontSize:11,color:T.accentDeep,fontFamily:FONT,fontWeight:600}}>KREDİ</span>
        <span style={{fontSize:15,fontWeight:800,color:T.accent,fontFamily:FONT,minWidth:20,textAlign:'center' as const}}>{creditBalanceProp}</span>
        <Button variant="soft" color={T.accent} onClick={()=>{ (window as any).__poyaisGoNav?.('kredi-yukle'); }} style={{padding:'4px 12px',fontSize:11}}>
          Kredi Yükle
        </Button>
      </div>
    </div>
  );

  // ── HOME ─────────────────────────────────────────────────────────────────
  if(view==='home') return(
    <div style={{fontFamily:FONT,minHeight:'100vh',background:T.surface}}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <CreditBadge/>
      <div style={{padding:'32px 24px 20px',textAlign:'center' as const}}>
        <Eyebrow color={T.accent}>AI Ses Üretici</Eyebrow>
        <h2 style={{fontSize:26,fontWeight:800,color:T.text,margin:'0 0 8px',letterSpacing:'-0.02em'}}>Ne yapmak istersiniz?</h2>
        <p style={{fontSize:13,color:T.textMid,margin:0}}>Metninizi sesli hale getirin veya sesinizi klonlayın.</p>
      </div>
      <div style={{padding:'0 24px 32px',display:'flex',justifyContent:'center',gap:20,flexWrap:'wrap' as const}}>
        {MODES.map(m=>(
          <button key={m.key} onClick={()=>setView(m.key as View)}
            style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:radius.xxl,padding:'32px 28px',cursor:'pointer',textAlign:'left' as const,transition:'all .2s',animation:'fadeIn 0.2s ease',minWidth:260,maxWidth:320,width:'100%',fontFamily:'inherit'}}
            onMouseEnter={e=>{ e.currentTarget.style.boxShadow='0 4px 20px rgba(201,168,76,0.15)'; e.currentTarget.style.borderColor=tint(T.accent,25); }}
            onMouseLeave={e=>{ e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor=T.border; }}>
            <div style={{fontSize:48,marginBottom:16}}>{m.icon}</div>
            <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:6}}>{m.label}</div>
            <div style={{fontSize:13,color:T.textMid,lineHeight:1.6}}>{m.desc}</div>
          </button>
        ))}
      </div>
      {/* Geçmiş */}
      {history.length>0&&(
        <div style={{padding:'0 24px 32px',maxWidth:800,margin:'0 auto'}}>
          <Eyebrow>SON OLUŞTURULAN SESLER</Eyebrow>
          <div style={{display:'flex',flexDirection:'column' as const,gap:8}}>
            {history.slice(0,5).map(item=>(
              <Card key={item.id} pad={12} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{item.text}</div>
                  <div style={{fontSize:10,color:T.textMid,marginTop:2}}>{item.voiceName} · {item.createdAt}</div>
                </div>
                {item.status==='done'&&item.audioUrl&&(
                  <div style={{display:'flex',gap:6}}>
                    <button onClick={()=>togglePlay(item)}
                      style={{width:30,height:30,borderRadius:'50%',background:playingId===item.id?tint(T.accent,10):T.card,border:`1px solid ${playingId===item.id?tint(T.accent,25):tint(T.accent,18)}`,color:playingId===item.id?T.accent:T.textMid,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {playingId===item.id?'⏸':'▶'}
                    </button>
                    <a href={item.audioUrl} download={`ses-${item.id}.mp3`}
                      style={{width:30,height:30,borderRadius:'50%',background:T.card,border:`1px solid ${tint(T.accent,18)}`,color:T.textMid,fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>⬇</a>
                  </div>
                )}
                {item.status==='pending'&&<Spinner/>}
                {item.status==='error'&&<span style={{fontSize:12,color:T.redText}}>❌</span>}
              </Card>
            ))}
          </div>
          <Button variant="ghost" onClick={()=>saveHistory([])} style={{marginTop:8,border:`1px solid ${tint(T.accent,18)}`,fontSize:10,padding:'6px 14px'}}>Geçmişi Temizle</Button>
        </div>
      )}
    </div>
  );

  // ── TTS / CLONE VIEW ──────────────────────────────────────────────────────
  const isTTS = view==='tts';
  return(
    <div style={{fontFamily:FONT,background:T.surface,minHeight:'100vh'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`}</style>
      <CreditBadge/>
      <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:12}}>
        <Button variant="ghost" onClick={()=>setView('home')} style={{padding:'6px 14px',fontSize:12}}>
          ← Geri
        </Button>
        <IconChip color={T.accent} size={36}><span style={{fontSize:16}}>{isTTS?'🔊':'🎭'}</span></IconChip>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{isTTS?'Metin → Ses':'Ses Klonlama'}</div>
          <div style={{fontSize:11,color:T.textMid}}>{isTTS?'Metninizi seçtiğiniz sesle seslendirin.':'Referans ses yükleyerek klonlayın.'}</div>
        </div>
      </div>

      <div style={{padding:'16px',display:'flex',flexDirection:'column' as const,gap:20,boxSizing:'border-box' as const,width:'100%',overflowX:'hidden' as const}}>
        {/* Sol */}
        <div style={{display:'flex',flexDirection:'column' as const,gap:14}}>
          {/* Metin */}
          <Card pad={16}>
            <Eyebrow>SESLENDİRİLECEK METİN</Eyebrow>
            <textarea value={text} onChange={e=>setText(e.target.value.slice(0,2500))}
              placeholder="Seslendirilecek metni buraya girin…" rows={5}
              style={{width:'100%',background:T.card,border:`1px solid ${tint(T.accent,18)}`,borderRadius:radius.sm,color:T.text,fontSize:13,padding:'9px 12px',resize:'vertical' as const,outline:'none',fontFamily:'inherit',lineHeight:1.6,boxSizing:'border-box' as const}}
              onFocus={e=>e.target.style.borderColor=tint(T.accent,50)}
              onBlur={e=>e.target.style.borderColor=tint(T.accent,18)}/>
            <div style={{fontSize:10,color:T.textMid,textAlign:'right' as const,marginTop:4}}>{text.length} / 2500</div>
          </Card>

          {/* TTS: Ses seçimi */}
          {isTTS&&(
            <Card pad={16}>
              <Eyebrow>SES SEÇİMİ {voicesLoading&&'— Yükleniyor…'}</Eyebrow>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,maxHeight:220,overflowY:'auto' as const,overflowX:'hidden' as const}}>
                {voices.map(v=>(
                  <div key={v.id} onClick={()=>setSV(v.id)}
                    style={{padding:'8px 10px',borderRadius:radius.sm,cursor:'pointer',background:selectedVoice===v.id?tint(T.accent,10):T.card,border:`1px solid ${selectedVoice===v.id?tint(T.accent,25):'transparent'}`,display:'flex',alignItems:'center',justifyContent:'space-between',gap:6,transition:'all .15s'}}>
                    <span style={{fontSize:11,fontWeight:selectedVoice===v.id?700:500,color:selectedVoice===v.id?T.accent:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const,flex:1}}>
                      {v.name.split(' - ')[0]}
                    </span>
                    {v.preview_url&&(
                      <button onClick={e=>{e.stopPropagation();previewVoice(v);}}
                        style={{width:22,height:22,borderRadius:'50%',border:`1px solid ${previewingId===v.id?tint(T.accent,25):tint(T.accent,20)}`,background:previewingId===v.id?tint(T.accent,10):'transparent',color:previewingId===v.id?T.accent:T.textMid,cursor:'pointer',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        {previewingId===v.id?'⏸':'▶'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Clone */}
          {!isTTS&&(
            <Card pad={16}>
              <Eyebrow>REFERANS SES DOSYASI</Eyebrow>
              <div onClick={()=>fileRef.current?.click()}
                style={{padding:'20px',border:`1.5px dashed ${cloneFile?tint(T.accent,25):tint(T.accent,25)}`,borderRadius:radius.md,textAlign:'center' as const,cursor:'pointer',background:cloneFile?tint(T.accent,10):T.card,transition:'all .2s'}}>
                {cloneFile?(
                  <div><div style={{fontSize:13,fontWeight:600,color:T.accent}}>🎵 {cloneFile.name}</div><div style={{fontSize:11,color:T.textMid,marginTop:4}}>{(cloneFile.size/1024).toFixed(1)} KB</div></div>
                ):(
                  <div><div style={{fontSize:28,marginBottom:6}}>🎙️</div><div style={{fontSize:13,color:T.text,fontWeight:600}}>WAV / MP3 yükle</div><div style={{fontSize:11,color:T.textMid,marginTop:3}}>3–30 sn önerilir.</div></div>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".wav,.mp3,.flac,.ogg" style={{display:'none'}} onChange={e=>setCloneFile(e.target.files?.[0]??null)}/>
              <div style={{marginTop:12}}>
                <Eyebrow>REFERANS METNİ (OPSİYONEL)</Eyebrow>
                <input value={cloneText} onChange={e=>setCloneText(e.target.value)} placeholder="Referans seste söylenen metin…"
                  style={{width:'100%',background:T.card,border:`1px solid ${tint(T.accent,18)}`,borderRadius:radius.sm,color:T.text,fontSize:12,padding:'9px 12px',outline:'none',boxSizing:'border-box' as const,fontFamily:'inherit'}}/>
              </div>
            </Card>
          )}

          {/* Parametreler */}
          <Tile pad={16}>
            <Eyebrow>SES PARAMETRELERİ</Eyebrow>
            <Grid min={140} gap={16}>
              <div>
                <div style={{fontSize:11,color:T.textMid,marginBottom:6}}>Kararlılık: <span style={{color:T.accent,fontWeight:700}}>{stability.toFixed(2)}</span></div>
                <input type="range" min={0} max={1} step={0.05} value={stability} onChange={e=>setStability(parseFloat(e.target.value))} style={{width:'100%',accentColor:T.accent}}/>
                <div style={{fontSize:10,color:T.textMid,marginTop:2}}>Düşük = daha ekspresif</div>
              </div>
              <div>
                <div style={{fontSize:11,color:T.textMid,marginBottom:6}}>Benzerlik: <span style={{color:T.accent,fontWeight:700}}>{similarity.toFixed(2)}</span></div>
                <input type="range" min={0} max={1} step={0.05} value={similarity} onChange={e=>setSimilarity(parseFloat(e.target.value))} style={{width:'100%',accentColor:T.accent}}/>
                <div style={{fontSize:10,color:T.textMid,marginTop:2}}>Yüksek = orijinale yakın</div>
              </div>
            </Grid>
          </Tile>

          <Button variant="solid" color={T.accent} onClick={handleSynthesize} disabled={loading||!text.trim()||(view==='clone'&&!cloneFile)}
            style={{width:'100%',padding:'12px',fontSize:13,gap:8,...(loading?{background:T.card,color:T.textMid,border:`1px solid ${tint(T.accent,18)}`,cursor:'not-allowed'}:{cursor:'pointer'})}}>
            {loading?<><Spinner/><span>Sentezleniyor…</span></>:<span>{isTTS?'Sesi Oluştur':'Sesi Klonla'}</span>}
          </Button>
        </div>

        {/* Geçmiş */}
        <div style={{display:'flex',flexDirection:'column' as const,gap:12,width:'100%'}}>
          <Eyebrow>Oluşturulan Sesler</Eyebrow>
          {history.length===0?(
            <Tile pad={16} style={{padding:'32px 16px',textAlign:'center' as const}}>
              <div style={{fontSize:28,marginBottom:8}}>🎙️</div>
              <div style={{fontSize:13,color:T.textMid}}>Henüz ses oluşturulmadı.</div>
            </Tile>
          ):(
            <div style={{display:'flex',flexDirection:'column' as const,gap:8}}>
              {history.map(item=>(
                <Card key={item.id} pad={12} style={{animation:'fadeIn 0.2s ease'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,color:T.text,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{item.text}</div>
                      <div style={{fontSize:10,color:T.textMid,marginTop:3}}>{item.voiceName} · {item.createdAt}</div>
                    </div>
                    <div style={{display:'flex',gap:5,flexShrink:0,alignItems:'center'}}>
                      {item.status==='pending'&&<Spinner/>}
                      {item.status==='error'&&<span style={{fontSize:10,color:T.redText}}>❌</span>}
                      {item.status==='done'&&item.audioUrl&&(
                        <>
                          <button onClick={()=>togglePlay(item)} style={{width:28,height:28,borderRadius:'50%',background:playingId===item.id?tint(T.accent,10):T.card,border:`1px solid ${playingId===item.id?tint(T.accent,25):tint(T.accent,18)}`,color:playingId===item.id?T.accent:T.textMid,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>
                            {playingId===item.id?'⏸':'▶'}
                          </button>
                          <a href={item.audioUrl} download={`ses-${item.id}.mp3`} style={{width:28,height:28,borderRadius:'50%',background:T.card,border:`1px solid ${tint(T.accent,18)}`,color:T.textMid,fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>⬇</a>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {history.length>0&&(
            <Button variant="ghost" onClick={()=>saveHistory([])} style={{border:`1px solid ${tint(T.accent,18)}`,fontSize:10,padding:'6px'}}>
              Geçmişi Temizle
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AISesTab;
